/* eslint-disable react/jsx-indent */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import { useIsAdmin, useIsUser } from 'hooks';
import * as Yup from 'yup';
import { VALIDATION, WAGON_UTENSILS } from 'helpers/constants';
import { hasMaxFractionDigits } from 'helpers/number';
import { Button, ButtonClose } from 'components/ui/form';
import { Input, Select, File } from 'components/ui/formik';
import History from '../history';
import Status from '../status';
import styles from '../../order.module.scss';

const roundShippedTon = (v) => (v != null && v !== '' ? Math.round(Number(v) * 100) / 100 : null);
const FORBIDDEN_NUMBER_KEYS = ['+', '-', 'e', 'E'];
const SUBMIT_CLICK_DELAY = 1000;

const FormValidationSchema = Yup.object().shape({
  payments: Yup.array().of(
    Yup.string()
      .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING)),
  ),
  additional: Yup.string()
    .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING)),
  comment: Yup.string()
    .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING)),
});

const EditFormValidationSchema = Yup.object().shape({
  shippedTon: Yup.number()
    .min(0, 'Не меньше 0')
    .test('maxDecimals', 'Не более двух знаков после запятой', val => hasMaxFractionDigits(val, 2)),
});

const Form = ({
  edit,
  tons,
  wagons,
  stations,
  contracts,
  statusHistory,
  userConsignees,
  applicationWagons,
  onSubmit,
  onClose,
  onUpdateStatus,
  onUpdateTon,
  onUpdateGy12,
  onChangeProduct,
  onChangeApplicationWagon,
  getCountWagon,
  initialValues,
  getIndex,
  profile,
}) => {
  const isUser = useIsUser();
  const isAdmin = useIsAdmin();
  const [savedGy12, setSavedGy12] = useState(initialValues.gy12 ?? '');
  const [savedTon, setSavedTon] = useState(initialValues.ton);
  const [savedShippedTon, setSavedShippedTon] = useState(initialValues.shippedTon);
  const [savedStatus, setSavedStatus] = useState(initialValues.status);
  const submitClickTimeoutRef = useRef(null);

  useEffect(() => {
    setSavedGy12(initialValues.gy12 ?? '');
    setSavedTon(initialValues.ton);
    setSavedShippedTon(initialValues.shippedTon);
    setSavedStatus(initialValues.status);
  }, [initialValues.gy12, initialValues.ton, initialValues.shippedTon, initialValues.status]);

  useEffect(() => () => {
    if (submitClickTimeoutRef.current) {
      clearTimeout(submitClickTimeoutRef.current);
    }
  }, []);

  const handleCreateSubmit = (handleSubmit) => {
    if (submitClickTimeoutRef.current) {
      return;
    }

    handleSubmit();
    submitClickTimeoutRef.current = setTimeout(() => {
      submitClickTimeoutRef.current = null;
    }, SUBMIT_CLICK_DELAY);
  };
  const preventForbiddenNumberSymbols = (event) => {
    if (FORBIDDEN_NUMBER_KEYS.includes(event.key)) {
      event.preventDefault();
    }
  };

  const renderTon = (values) => {
    const hasTonLimits = (
      tons !== undefined
      && tons[values.contractProductId] !== undefined
      && tons[values.contractProductId][values.type] !== undefined
    );

    if (!hasTonLimits && !edit) {
      return null;
    }

    const applicationWagonIndex = getIndex(applicationWagons, values.applicationWagonId);
    const {
      tonMax = 0,
    } = applicationWagonIndex >= 0 ? applicationWagons[applicationWagonIndex] : {};

    let tonMaxValue = edit ? Number(edit.ton) : undefined;
    if (hasTonLimits) {
      const tonProduct = tons[values.contractProductId][values.type];
      const tonResult = tonProduct > tonMax ? tonMax : tonProduct;
      tonMaxValue = edit ? Number(edit.ton) : tonResult;
    }

    const hasWagon = wagons.some(({ id }) => Number(id) === Number(values.wagonId));
    const wagonCount = hasWagon ? getCountWagon(values) : (Number(values.wagonCount) || 0);

    return (
      <>
        <div className={[styles.item, styles.num, styles.small].join(' ')}>
          <Input
            disabled={Boolean(edit) && isUser}
            label="Количество, тонн"
            type="number"
            name="ton"
            min={0}
            max={Number.isFinite(tonMaxValue) ? tonMaxValue : undefined}
            step={1}
            onKeyDown={preventForbiddenNumberSymbols}
            onMax={() => {
              const message = 'Превышен лимит';
              const toastId = 'maxSupplyTon';

              if (!toast.isActive(toastId)) {
                toast.error(message, { toastId });
              }
            }}
          />
        </div>
        <div className={[styles.item, styles.small].join(' ')}>
          <Input disabled={Boolean(edit)} label="Количество вагонов" type="hidden" name="wagonCount" />
          <p className={styles.itemData}>{wagonCount}</p>
        </div>
      </>
    );
  };

  return (
    <>
      <div className={styles.heading}>
        <p className={styles.orderName}>{profile.name}</p>
        <p className={styles.orderWallet}>
          {contracts[0]?.balance || 0}
          {' '}
          руб
        </p>
      </div>
      <Formik
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={edit ? EditFormValidationSchema : FormValidationSchema}
      >
        {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
          const applicationWagonIndex = getIndex(applicationWagons, values.applicationWagonId);
          const {
            contractProduct = [],
          } = applicationWagonIndex >= 0 ? applicationWagons[applicationWagonIndex] : {};
          const currentContract = contracts.find(({ id }) => id === Number(values.contractId));
          const hasDeferral = Number(currentContract?.isPaymentDeferralProvided) === 1;
          const deferralText = hasDeferral
            ? moment(currentContract.paymentDeferralEndDate).format('DD.MM.YYYY')
            : 'Нет';

          const isChangedStatus = edit && Number(savedStatus) !== Number(values.status);
          const isChangedTon = edit
            && (Number(savedTon) !== Number(values.ton)
              || roundShippedTon(savedShippedTon) !== roundShippedTon(values.shippedTon));
          const isChangedGy12 = edit
            && isAdmin
            && String(savedGy12) !== String(values.gy12 ?? '');

          const productIndex = getIndex(contractProduct, values.contractProductId);
          const {
            product: { gng } = {},
          } = productIndex >= 0 ? contractProduct[productIndex] : {};

          return (
            <>
              <form onSubmit={handleSubmit}>
                <div className={styles.orderContent}>
                  <div className={styles.orderWrap}>
                    {applicationWagons.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select
                          disabled={Boolean(edit)}
                          label="Заявка на вагон"
                          name="applicationWagonId"
                          onChange={e => onChangeApplicationWagon(e, setFieldValue)}
                        >
                          {applicationWagons.map(({ id, date }) => (
                            <option key={id} value={id}>
                              {`№${id} от ${moment(date).format('DD.MM.YYYY')}`}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {contracts.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled label="Договор" name="contractId">
                          {contracts.map(({ id, name, date }) => (
                            <option key={id} value={id}>
                              {name}
                              {' от '}
                              {moment(date).format('DD.MM.YYYY')}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    <div className={styles.item}>
                      <p className={styles.itemName}>Отсрочка платежа</p>
                      <p className={styles.itemData}>{deferralText}</p>
                    </div>
                    {!isUser ? (
                      <>
                        {profile.uAddress ? (
                          <div className={styles.item}>
                            <p>
                              {profile.uAddress}
                              <br />
                              {`УНП ${profile.inn}`}
                            </p>
                          </div>
                        ) : null}
                        {(values.fio || values.email || values.phone) ? (
                          <p className={styles.orderTitle}>Исполнитель</p>
                        ) : null}
                        {values.fio && (
                          <div className={styles.item}>
                            <Input label="ФИО" placeholder="ФИО" name="fio" disabled />
                          </div>
                        )}
                        {values.email && (
                          <div className={styles.item}>
                            <Input label="Email" placeholder="Email" name="email" type="email" disabled />
                          </div>
                        )}
                        {values.phone && (
                          <div className={styles.item}>
                            <Input label="Телефон" placeholder="+375 (**) *** - ** - **" name="phone" type="tel" disabled />
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                  <div className={styles.orderWrap}>
                    {stations.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled label="Станция назначения" name="stationId">
                          {stations.map(({ id, name, code }) => (
                            <option key={id} value={id}>{`${name} ${code ? `(${code})` : ''}`}</option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    <div className={[styles.item, styles.select].join(' ')}>
                      <Input label="Группа" name="productGroup" disabled placeholder="Группа" />
                    </div>
                    {contractProduct.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled={Boolean(edit)} label="Продукт" name="contractProductId" onChange={() => onChangeProduct(setFieldValue)}>
                          {contractProduct.map(({ id, product: { name } }) => (
                            <option key={id} value={id}>{name}</option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {gng ? (
                      <div className={styles.item}>
                        <p className={styles.itemName}>Код ГНГ</p>
                        <p className={styles.itemData}>{gng}</p>
                      </div>
                    ) : null}
                    {wagons.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled label="Вид вагона" name="wagonId">
                          {wagons.map(({ id, name, ton }) => (
                            <option key={id} value={id}>
                              {name}
                              {' '}
                              (
                                {ton}
                                {' '}
                                тонн
                              )
                            </option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    {userConsignees.length ? (
                      <div className={[styles.item, styles.select].join(' ')}>
                        <Select disabled label="Грузополучатель" name="userConsigneeId">
                          <option key="default" value="default">Прочие (9999)</option>
                          {userConsignees.map(({ id, consignee: { name } }) => (
                            <option key={id} value={id}>{name}</option>
                          ))}
                        </Select>
                      </div>
                    ) : null}
                    <div className={styles.item}>
                      <Input
                        disabled
                        label="ЖД код грузополучателя"
                        name="codeGd"
                        placeholder="ЖД код грузополучателя"
                      />
                    </div>
                    {edit ? (
                      <div className={[styles.item, styles.num].join(' ')}>
                        <Input
                          label="Отгружено"
                          type="number"
                          name="shippedTon"
                          disabled={isUser}
                          min={0}
                          max={Number(values.ton) || 0}
                          step={0.01}
                          onKeyDown={preventForbiddenNumberSymbols}
                        />
                      </div>
                    ) : null}
                    {renderTon(values)}
                    <div className={styles.item}>
                      <Select disabled label="Принадлежность вагонов" name="utensils">
                        {WAGON_UTENSILS.list().map(value => (
                          <option key={value} value={value}>
                            {WAGON_UTENSILS.toString(value)}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className={styles.item}>
                      <Input disabled={Boolean(edit)} label="Платежное поручение за продукцию" placeholder="Номер, дата сумма" name="payments[0]" />
                    </div>
                    <div className={styles.item}>
                      <Input disabled={Boolean(edit)} label="Платежное поручение за жд тариф" placeholder="Номер, дата сумма" name="payments[1]" />
                    </div>
                    <div className={styles.item}>
                      <Input disabled={Boolean(edit)} label="Дополнительная информация по вагонам" name="additional" placeholder="Дополнительная информация по вагонам" />
                    </div>
                    <div className={styles.item}>
                      <Input disabled={Boolean(edit)} label="Примечание" name="comment" placeholder="Примечание" />
                    </div>
                    <div className={styles.item}>
                      <Input
                        disabled={!edit || !isAdmin}
                        label="Номер ГУ-12"
                        name="gy12"
                        placeholder="Номер ГУ-12"
                      />
                    </div>
                    {edit ? (
                      <Status edit={edit} values={values} />
                    ) : (
                      <div className={styles.item}>
                        <File
                          accept=".jpg, .jpeg, .png, .xlsx, .xls, .pdf"
                          name="file"
                          label={(
                            <>
                              <span>Загрузить</span>
                              {' '}
                              письмо госорганов управления
                            </>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </form>
              {statusHistory.length ? (
                <div className={styles.history}>
                  <div className={styles.historyName}>История изменения статусов</div>
                  <div className={styles.historyTable}>
                    <History data={statusHistory} />
                  </div>
                </div>
              ) : null}
              <div className={styles.footer}>
                <div>
                  <ButtonClose onClick={() => onClose()}>{edit ? 'Закрыть' : 'Отменить'}</ButtonClose>
                </div>
                <div className={styles.buttons}>
                  {edit && isChangedStatus ? (
                    <Button
                      key={0}
                      onClick={async () => {
                        const hasOtherChanges = isChangedTon || isChangedGy12;
                        const response = await onUpdateStatus(values);
                        if (response) {
                          setSavedStatus(values.status);
                          if (!hasOtherChanges) {
                            onClose(true);
                          }
                        }
                      }}
                      type="button"
                    >
                      Обновить статус
                    </Button>
                  ) : null}
                  {edit && isChangedTon ? (
                    <Button
                      key={1}
                      onClick={async () => {
                        const hasOtherChanges = isChangedStatus || isChangedGy12;
                        const response = await onUpdateTon(values);
                        if (response) {
                          setSavedTon(values.ton);
                          setSavedShippedTon(values.shippedTon);
                          if (!hasOtherChanges) {
                            onClose(true);
                          }
                        }
                      }}
                      type="button"
                    >
                      Обновить количество тонн
                    </Button>
                  ) : null}
                  {isChangedGy12 ? (
                    <Button
                      key={2}
                      onClick={async () => {
                        const hasOtherChanges = isChangedStatus || isChangedTon;
                        const response = await onUpdateGy12(values);
                        if (response) {
                          setSavedGy12(values.gy12 ?? '');
                          if (!hasOtherChanges) {
                            onClose(true);
                          }
                        }
                      }}
                      type="button"
                    >
                      Сохранить номер ГУ-12
                    </Button>
                  ) : null}
                  {!edit ? (
                    <Button
                      key={3}
                      onClick={() => handleCreateSubmit(handleSubmit)}
                      type="button"
                      disabled={isSubmitting}
                    >
                      Оформить
                    </Button>
                  ) : null}
                </div>
              </div>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default Form;
